import { Controller } from '@hotwired/stimulus'
import updateValidationUI, {
  ValidationParent,
} from '../../helpers/controllerHelpers/customValidationUI'
import {
  connectUserData,
  doNotStoreComponentDataInCloud,
  getComponentContext,
  lifecycleTrigger,
} from '../../helpers/controllerHelpers/utils'
import { env } from '../../helpers/env'
import jitsu from '../../helpers/jitsu'
import { generateId, initAndWaitForSupabaseClient } from '../../helpers/utils'
import { cloneDeep } from 'lodash'

import type { FileUpload, UserDataValue } from '../../api/models/types'
import { fileDataType, validateFileUpload } from '../../helpers/validation'

const WORKER_NAME = 'upload-to-r2-bucket--public-flow-assets'

export default class extends Controller {
  static targets = ['uploader', 'validation', 'filelist', 'input']
  static values = { validation: Boolean }

  disconnectUserData?: () => void
  flowId: string | null
  component: FileUpload
  declare hasFilelistTarget: boolean
  declare filelistTarget: HTMLElement
  declare userDataValue: UserDataValue
  flowEl: Element | null
  declare inputTarget: HTMLFormElement

  connect() {
    const flowEl = this.element.closest(`[data-controller*="flow"]`)
    this.flowId = flowEl && flowEl.getAttribute('data-flow-id-value')
    const { component } = getComponentContext(this)
    this.component = component as FileUpload
    this.disconnectUserData = connectUserData(this, this.userDataValueChanged)
    this.flowEl = flowEl
    lifecycleTrigger(this, 'mounted')
  }

  disconnect() {
    lifecycleTrigger(this, 'unmounted')
    if (typeof this.disconnectUserData === 'function') this.disconnectUserData()
  }

  /*
   * Triggers whenever the value of userData changes.
   * Updates the UI based on the new data and makes error validation messages visible if necessary.
   *
   * @param newData - The new user data object containing the updated values for the component.
   */
  userDataValueChanged(newData: { [x: string]: any }) {
    const key = this.element.getAttribute('data-component-key-value') ?? ''
    const fileContent = newData?.[key] as fileDataType | fileDataType[] | undefined

    const message = this.getValidationMessage(newData[key])

    updateValidationUI(
      this,
      message,
      this.element as ValidationParent,
      this.inputTarget as unknown as HTMLObjectElement
    )

    if (fileContent) {
      Array.isArray(fileContent)
        ? fileContent.forEach((file) => this.updateUI(file.status, file))
        : this.updateUI(fileContent.status, fileContent)
    }
  }

  /*
   * Returns a validation message based on the file content and component settings.
   * @param fileContent - The content of the file(s) being validated, which can be a single file or an array of files.
   * @return A string containing the validation message. Empty string if validation passes.
   */
  getValidationMessage(fileContent: fileDataType | fileDataType[] | undefined): string {
    const { failedValidation } = validateFileUpload(this.component, fileContent)

    if (failedValidation === 'required') {
      return this.component.empty_invalid_message ?? 'This field is required'
    }

    if (failedValidation === 'maxFilesLimit') {
      return (
        this.component.invalid_message ??
        `Please only upload up to a maximum of ${this.component.files_max_limit} files`
      )
    }

    return ''
  }

  /*
   * Updates the file data in the userDataValue and triggers the onChange actions.
   * @param fileData - The file data to update, which includes id, name, status, and url.
   */
  update(fileData: fileDataType) {
    const componentKey = this.element.getAttribute('data-component-key-value') || ''
    const previousValue: fileDataType | fileDataType[] | undefined = cloneDeep(
      this.userDataValue[componentKey]
    )

    let value = previousValue

    if (!this.component.multiple) {
      value = fileData.status !== 'deleted' ? fileData : undefined
    } else {
      if (!value) value = []
      if (!Array.isArray(value)) value = [value]

      const index = value.findIndex((item) => item.id === fileData.id)
      const isFileInUserData = index > -1

      if (isFileInUserData && fileData.status === 'deleted') value.splice(index, 1)
      if (isFileInUserData && fileData.status !== 'deleted') value[index] = fileData
      if (!isFileInUserData && fileData.status === 'deleted') return
      if (!isFileInUserData && fileData.status !== 'deleted') value.push(fileData)
    }

    this.dispatch('update', {
      detail: {
        key: componentKey,
        value,
        callback: () => this.triggerOnChangeActions(value),
      },
    })

    if (!doNotStoreComponentDataInCloud(this.component)) {
      jitsu.track('file:uploaded', {
        flow_id: this.flowId,
        file_uploader_id: this.component.id,
        file_uploader_key: this.component.key,
      })
    }
  }

  /*
   * Handles file(s) upload event if the attribute "FileUploader.uploadFile" is set to true.
   * It uploads the file(s) to both Supabase and Cloudflare Workers, updating the UI with the status of each file.
   * @param event - The event object containing the file(s) to be uploaded.
   */
  async uploadFile(event: { target: { files: FileList; value: string | null } }) {
    const filesToUpload = this.component.multiple
      ? Array.from(event.target.files)
      : [event.target.files[0]]

    filesToUpload.forEach((file) => {
      const fileId = generateId('file')

      //To test locally comment 'local' out
      if (['dev', 'ci', 'test', 'local'].includes(env())) {
        const url = URL.createObjectURL(file)
        const fileData = { id: fileId, name: file.name, status: 'done', url: url || 'in_memory' }
        this.updateUI('done', fileData)
        this.update(fileData)
        return
      }

      if (this.component.uploadFile) {
        const fileName = `${file.name.split('.')[0]}_${('_' + Math.random()).slice(3)}.${file.name
          .split('.')
          .slice(1)
          .join('.')}`.replace(/[^a-zA-Z0-9-_.]/g, '')

        function updateStatus(status: string, url?: string) {
          this.update({
            id: fileId,
            name: fileName,
            status,
            url,
          })
        }

        this.uploadToSupabase(file, fileName, updateStatus.bind(this))
        this.uploadToCloudflare(file, fileName, updateStatus.bind(this))

        event.target.value = null
      }
    })
  }

  async uploadToCloudflare(
    file: File,
    fileName: string,
    updateStatus: (status: string, url?: string) => void
  ) {
    updateStatus('uploading')
    return await fetch(
      `https://${WORKER_NAME}.heysavvy.workers.dev/${decodeURIComponent(fileName)}`,
      {
        method: 'POST',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        updateStatus('done', data.data.url)
      })
      .catch((error) => {
        console.error('Error Uploading to Cloudflare:', error)
        updateStatus('error')
      })
  }

  async uploadToSupabase(
    file: File,
    fileName: string,
    updateStatus: (status: string, url?: string) => void
  ) {
    const supabaseClient = await initAndWaitForSupabaseClient()
    const { data, error } = await supabaseClient.storage
      .from('uploaded_assets')
      .upload(`${this.flowId}/${fileName}`, file)

    if (error) {
      console.error(`Error Uploading ${fileName}:`, error)
    } else {
      try {
        const { data: uploadedData, error: uploadError } = await supabaseClient.storage
          .from('uploaded_assets')
          .getPublicUrl(`${this.flowId}/${fileName}`)

        if (uploadError) {
          console.error(uploadError)
          return
        }

        const url = uploadedData.publicUrl
      } catch (error) {
        console.error(error)
      }
    }
  }

  /*
   * Adds or removes file items in the UI based on their status.
   * Each file is represented by a span element with a link to the file on the left, the name in the middle, and a delete icon on the right.
   *
   * @param status - The status of the file(s) being updated (e.g., 'uploading', 'done', 'deleted').
   * @param fileOrFiles - A single file or an array of files to update in the UI.
   */
  updateUI(status: string, fileOrFiles: fileDataType | fileDataType[]) {
    if (!this.hasFilelistTarget) return
    const files = Array.isArray(fileOrFiles) ? [...fileOrFiles] : [fileOrFiles]

    //* For loop - over files
    files.forEach((file) => {
      let itemElement = this.filelistTarget.querySelector(`[data-file-id="${file.id}"]`)

      if (itemElement) {
        const fileName = itemElement.querySelector('.file-name') as HTMLElement
        const downloadLinkA = itemElement.querySelector('.paper-clip') as HTMLElement

        if (fileName) {
          fileName.innerText = String(file.name)
          fileName.setAttribute('data-name', String(file.name))
        }

        itemElement.setAttribute('data-status', status)

        if (downloadLinkA) {
          downloadLinkA.style.display = file.status === 'uploading' ? 'none' : 'inline-block'
          downloadLinkA.setAttribute('href', file.url ?? '#')
          if (!!file.url) downloadLinkA.setAttribute('target', '_blank')
        }
        if (status === 'deleted') itemElement.remove()
      } else {
        const item = document.createElement('span')
        item.classList.add('ant-upload-list-item-info')
        item.setAttribute('data-file-id', String(file.id))
        item.style.position = 'relative'
        item.style.display = 'block'

        const downloadLinkA = document.createElement('a')
        downloadLinkA.ariaLabel = 'icon: paper-clip'
        downloadLinkA.classList.add('paper-clip', 'anticon', 'anticon-paper-clip')
        downloadLinkA.style.display = file.status === 'uploading' ? 'none' : 'inline-block'
        downloadLinkA.setAttribute('href', file.url ?? '#')
        if (!!file.url) downloadLinkA.setAttribute('target', '_blank')

        const paperClipSvg = this.createSvgIcon('paperclip', true)

        downloadLinkA.appendChild(paperClipSvg)
        item.appendChild(downloadLinkA)

        const fileName = document.createElement('span')
        fileName.innerText = String(file.name)
        fileName.setAttribute('data-name', String(file.name))
        fileName.classList.add('file-name', 'ant-upload-list-item-name')
        item.appendChild(fileName)

        const fileActions = document.createElement('span')
        fileActions.classList.add('file-actions', 'ant-upload-list-item-card-actions')

        const anchor = document.createElement('a')
        anchor.setAttribute('data-action', 'click->components--file-upload-v2#deleteFile')
        anchor.setAttribute('data-file-id', String(file.id))

        const italicAnchorEl = document.createElement('i')
        italicAnchorEl.ariaLabel = 'icon: delete'
        italicAnchorEl.classList.add('anticon', 'anticon-delete', 'delete-file')
        italicAnchorEl.tabIndex = -1

        const anchorSvg = this.createSvgIcon('trash', true)
        anchorSvg.setAttribute('data-icon', 'delete')

        italicAnchorEl.appendChild(anchorSvg)
        anchor.appendChild(italicAnchorEl)
        fileActions.appendChild(anchor)
        item.appendChild(fileActions)

        if (!this.component.multiple) this.filelistTarget.innerHTML = ''

        this.filelistTarget.appendChild(item)
      }
    })
  }

  createSvgIcon(iconName: string, clickable: boolean = false): HTMLElement {
    const svg = document.createElement('svg')

    svg.setAttribute('data-controller', 'icon')
    svg.setAttribute('data-icon-name-value', iconName)
    svg.setAttribute('data-icon-library-value', 'fa')
    svg.classList.add('Icon')
    svg.setAttribute('viewBox', '64 64 896 896')
    svg.setAttribute('aria-hidden', 'true')
    svg.setAttribute('focusable', 'false')
    svg.setAttribute('fill', 'currentColor')
    svg.setAttribute('height', '1em')
    svg.setAttribute('width', '1em')

    if (clickable) svg.style.cursor = 'pointer'

    return svg
  }

  triggerOnChangeActions(newValue: any) {
    if (!newValue) newValue = null
    //* I trigger onchange actions after waiting for the userData update to complete
    if (this.component.outputs_onchange) {
      const actions = this.component.outputs_onchange
      this.dispatch('actions', {
        detail: {
          actions: actions,
          component: this.component,
        },
      })
    }
  }

  async accessibilityFileUpload(event) {
    //* Preventing scrolling
    if (
      (event as KeyboardEvent).key === ' ' ||
      (event as KeyboardEvent).key === 'ArrowDown' ||
      (event as KeyboardEvent).key === 'ArrowUp'
    ) {
      event.preventDefault()
    }

    if ((event as KeyboardEvent).key === 'Enter' || (event as KeyboardEvent).key === ' ') {
      const input = this.element.querySelector('input')
      input?.click()
    }
  }

  /*
   * Deletes a file from the file list and updates the user data value.
   * Triggered by clicking the delete icon next to a file in the file list.
   */
  deleteFile(event: Event) {
    event.preventDefault()
    const anchor = event.currentTarget as HTMLElement

    const listItem = anchor.closest('.ant-upload-list-item-info')
    const fileId = anchor.getAttribute('data-file-id')

    if (fileId) {
      this.update({ id: fileId, status: 'deleted' })
      if (listItem) listItem.remove()
    }
  }
}
